import { Check } from 'lucide-react';
import Toggle from '../components/Toggle';

// ─────────────────────────────────────────
// VEHICLE CHECK LIST
// Inline list of named vehicles with checkboxes — used to pick
// which vehicles qualify for a given feature/discount.
// Used only inside Step5 so it lives here.
// ─────────────────────────────────────────
function VehicleCheckList({ autos, selectedIds, onToggle }) {
  const namedAutos = autos.filter(a => a.year || a.make || a.model);
  if (!namedAutos.length) return <p className="text-xs text-slate-400 italic">No vehicles added yet.</p>;
  return (
    <div className="space-y-2 mt-2 ml-1">
      {namedAutos.map((a, i) => {
        const label = [a.year, a.make, a.model].filter(Boolean).join(' ') || `Vehicle ${i + 1}`;
        const isSelected = selectedIds.includes(i);
        return (
          <label key={i} className="flex items-center gap-2.5 cursor-pointer select-none">
            <div
              onClick={() => onToggle(i)}
              className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'
              }`}
            >
              {isSelected && <Check size={10} strokeWidth={3} className="text-white" />}
            </div>
            <span className="text-sm text-slate-700">{label}</span>
          </label>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────
// STEP 5 — VEHICLE DETAILS
// "Does any vehicle..." questions. Each top-level toggle controls
// whether ALL named vehicles get the field set; the per-vehicle
// checklist below it lets the client narrow down which cars qualify.
// ─────────────────────────────────────────
export default function Step5VehicleDetails({ autos, updateAuto }) {
  const namedAutos = autos.filter(a => a.year || a.make || a.model);

  // "Does any vehicle have X?" — true if any named auto has that flag
  const hasAnyCustomEquip       = autos.some(a => a.hasCustomEquipment);
  const hasAnyGarage            = autos.some(a => a.keptInGarage);
  const hasAnyVacation          = autos.some(a => a.vacationUse);
  const hasAnyRegistered        = autos.some(a => a.registeredInState);
  const hasAnyVinEtching        = autos.some(a => a.antitheftVinEtching);
  const hasAnyVehicleRecovery   = autos.some(a => a.antitheftVehicleRecovery);
  const hasAnyDisabling         = autos.some(a => a.antitheftDisablingDevice);

  // Translate the namedAutos index back to the absolute autos[] index, then update.
  function toggleVehicleField(namedIndex, field, value) {
    let count = -1;
    for (let i = 0; i < autos.length; i++) {
      if (autos[i].year || autos[i].make || autos[i].model) {
        count++;
        if (count === namedIndex) { updateAuto(i, field, value); return; }
      }
    }
  }

  // Get the named-index list of autos that have a given boolean field set.
  function getSelectedIds(field) {
    const ids = [];
    let count = -1;
    for (let i = 0; i < autos.length; i++) {
      if (autos[i].year || autos[i].make || autos[i].model) {
        count++;
        if (autos[i][field]) ids.push(count);
      }
    }
    return ids;
  }

  // Toggle a single vehicle's flag (used by the per-vehicle checklists).
  function onToggleField(namedIndex, field) {
    let count = -1;
    for (let i = 0; i < autos.length; i++) {
      if (autos[i].year || autos[i].make || autos[i].model) {
        count++;
        if (count === namedIndex) { updateAuto(i, field, !autos[i][field]); return; }
      }
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
      <div>
        <h2 className="text-base font-semibold text-slate-800 mb-1">Vehicle Details</h2>
        <p className="text-sm text-slate-500">A few quick questions about your vehicles. Select which ones apply.</p>
      </div>

      {/* Custom equipment */}
      <div className="space-y-2">
        <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
          <Toggle
            label="Does any vehicle have custom or additional equipment to be insured?"
            checked={hasAnyCustomEquip}
            onChange={v => {
              namedAutos.forEach((_, i) => {
                toggleVehicleField(i, 'hasCustomEquipment', v);
                if (!v) toggleVehicleField(i, 'customEquipmentAmount', '');
              });
            }}
          />
        </div>
        {hasAnyCustomEquip && (
          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Which vehicles have custom equipment?</p>
            {namedAutos.map((a, i) => {
              const label = [a.year, a.make, a.model].filter(Boolean).join(' ') || `Vehicle ${i + 1}`;
              return (
                <div key={i} className="space-y-2">
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <div
                      onClick={() => {
                        toggleVehicleField(i, 'hasCustomEquipment', !a.hasCustomEquipment);
                        if (a.hasCustomEquipment) toggleVehicleField(i, 'customEquipmentAmount', '');
                      }}
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${a.hasCustomEquipment ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}`}
                    >
                      {a.hasCustomEquipment && <Check size={10} strokeWidth={3} className="text-white" />}
                    </div>
                    <span className="text-sm text-slate-700">{label}</span>
                  </label>
                  {a.hasCustomEquipment && (
                    <div className="ml-6">
                      <label className="block text-xs font-medium text-slate-500 mb-1">Coverage amount for custom equipment ($)</label>
                      <input
                        type="number"
                        value={a.customEquipmentAmount || ''}
                        onChange={e => toggleVehicleField(i, 'customEquipmentAmount', e.target.value)}
                        placeholder="e.g. 5000"
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Garage */}
      <div className="space-y-2">
        <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
          <Toggle
            label="Is any vehicle kept in a garage with 24-hour security?"
            checked={hasAnyGarage}
            onChange={v => namedAutos.forEach((_, i) => toggleVehicleField(i, 'keptInGarage', v))}
          />
        </div>
        {hasAnyGarage && (
          <div className="border border-slate-200 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Which vehicles are kept in a secured garage?</p>
            <VehicleCheckList autos={autos} selectedIds={getSelectedIds('keptInGarage')} onToggle={i => onToggleField(i, 'keptInGarage')} />
          </div>
        )}
      </div>

      {/* Vacation use */}
      <div className="space-y-2">
        <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
          <Toggle
            label="Is any vehicle used primarily as a vacation or seasonal vehicle?"
            checked={hasAnyVacation}
            onChange={v => namedAutos.forEach((_, i) => toggleVehicleField(i, 'vacationUse', v))}
          />
        </div>
        {hasAnyVacation && (
          <div className="border border-slate-200 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Which vehicles are vacation use?</p>
            <VehicleCheckList autos={autos} selectedIds={getSelectedIds('vacationUse')} onToggle={i => onToggleField(i, 'vacationUse')} />
          </div>
        )}
      </div>

      {/* Registered in home state */}
      <div className="space-y-2">
        <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
          <Toggle
            label="Are all your vehicles registered in your home state?"
            checked={hasAnyRegistered}
            onChange={v => namedAutos.forEach((_, i) => toggleVehicleField(i, 'registeredInState', v))}
          />
        </div>
        {hasAnyRegistered && (
          <div className="border border-slate-200 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Which vehicles are registered in your home state?</p>
            <VehicleCheckList autos={autos} selectedIds={getSelectedIds('registeredInState')} onToggle={i => onToggleField(i, 'registeredInState')} />
          </div>
        )}
      </div>

      {/* Anti-theft */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-slate-700">Anti-Theft Devices</p>
        <p className="text-xs text-slate-500">Select any anti-theft devices installed on your vehicles, then choose which cars they apply to.</p>

        <div className="space-y-2">
          <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
            <Toggle
              label="VIN etching on any vehicle?"
              checked={hasAnyVinEtching}
              onChange={v => namedAutos.forEach((_, i) => toggleVehicleField(i, 'antitheftVinEtching', v))}
            />
          </div>
          {hasAnyVinEtching && (
            <div className="border border-slate-200 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Which vehicles have VIN etching?</p>
              <VehicleCheckList autos={autos} selectedIds={getSelectedIds('antitheftVinEtching')} onToggle={i => onToggleField(i, 'antitheftVinEtching')} />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
            <Toggle
              label="GPS vehicle recovery system on any vehicle? (e.g. LoJack)"
              checked={hasAnyVehicleRecovery}
              onChange={v => namedAutos.forEach((_, i) => toggleVehicleField(i, 'antitheftVehicleRecovery', v))}
            />
          </div>
          {hasAnyVehicleRecovery && (
            <div className="border border-slate-200 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Which vehicles have a recovery system?</p>
              <VehicleCheckList autos={autos} selectedIds={getSelectedIds('antitheftVehicleRecovery')} onToggle={i => onToggleField(i, 'antitheftVehicleRecovery')} />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
            <Toggle
              label="Engine disabling device on any vehicle? (e.g. kill switch, immobilizer)"
              checked={hasAnyDisabling}
              onChange={v => namedAutos.forEach((_, i) => toggleVehicleField(i, 'antitheftDisablingDevice', v))}
            />
          </div>
          {hasAnyDisabling && (
            <div className="border border-slate-200 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Which vehicles have a disabling device?</p>
              <VehicleCheckList autos={autos} selectedIds={getSelectedIds('antitheftDisablingDevice')} onToggle={i => onToggleField(i, 'antitheftDisablingDevice')} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}